import { useCallback, useEffect, useState, useRef } from 'react';
import '../../Styles/index.css';
import izenuAPI from '../../services/izenuAPI';
import openArtistInApp from './openArtistInApp';
import EditUserModal from './EditUserModal';

const CDN_URL = process.env.REACT_APP_CDN_URL || 'https://dpagz47n4g1lw.cloudfront.net';

export default function Artists() {
    const [artists, setArtists] = useState([]);
    const [distributors, setDistributors] = useState([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [distributorFilter, setDistributorFilter] = useState('');
    const [loading, setLoading] = useState(true);
    const [editArtist, setEditArtist] = useState(null);
    const searchRef = useRef(null);

    const fetchArtists = useCallback(async (page, search, distId) => {
        setLoading(true);
        try {
            let url = `/admin/artist/list.php?page=${page}&limit=50`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            if (distId) url += `&distributor_id=${distId}`;

            const { data: res } = await izenuAPI.get(url);
            if (res.response) {
                setArtists(res.artists);
                setTotal(res.total);
                setTotalPages(res.pages);
                if (res.distributors) setDistributors(res.distributors);
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchArtists(currentPage, searchTerm, distributorFilter);
        }, searchTerm ? 400 : 0);
        return () => clearTimeout(timer);
    }, [currentPage, searchTerm, distributorFilter, fetchArtists]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleDistributorFilter = (e) => {
        setDistributorFilter(e.target.value);
        setCurrentPage(1);
    };

    const impersonateArtist = useCallback(async (artist) => {
        if (!window.confirm(`Entrar no painel de "${artist.artist_name || artist.name}"?`)) return;
        try {
            const { data: res } = await izenuAPI.post('/admin/artist/impersonate.php', {
                user_id: artist.user_id
            });
            if (!res.response) {
                alert(res.message || 'Erro ao impersonar artista');
                return;
            }
            // Backup admin session
            localStorage.setItem('admin_backup_token', localStorage.getItem('token'));
            localStorage.setItem('admin_backup_type', localStorage.getItem('type'));
            localStorage.setItem('admin_backup_id', localStorage.getItem('id'));
            localStorage.setItem('admin_backup_name', localStorage.getItem('name'));
            localStorage.setItem('admin_backup_user', localStorage.getItem('user'));
            // Switch to artist session
            localStorage.setItem('token', res.token);
            localStorage.setItem('type', 'artist');
            localStorage.setItem('id', res.artist.user_id);
            localStorage.setItem('name', res.artist.name);
            localStorage.setItem('user', JSON.stringify(res.artist));
            localStorage.setItem('impersonating', 'true');
            localStorage.setItem('impersonating_name', res.artist.artist_name || res.artist.name);
            // Reload to switch router
            window.location.href = '/';
        } catch (error) {
            console.log(error);
            alert('Erro ao impersonar artista');
        }
    }, []);

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    return (
        <div className="p-10">
            {/* Header */}
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 24, fontWeight: 700, color: '#333', marginBottom: 8 }}>
                    Artistas
                </h2>
                <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, marginBottom: 12 }}>
                    Todos os artistas registados na plataforma. Pesquise por nome ou email para abrir o Estúdio de qualquer artista.
                </p>
                <span style={{ fontSize: 13, color: '#6366f1', fontWeight: 600 }}>
                    {total.toLocaleString('pt-PT')} {total === 1 ? 'artista' : 'artistas'}
                    {distributorFilter ? ' nesta distribuidora' : ' na plataforma'}
                </span>
            </div>

            {/* Filters */}
            <div className="flex gap-4 mb-4 items-end flex-wrap">
                <div style={{ maxWidth: 400, flex: 1 }}>
                    <div className="search-distributors-input">
                        <input
                            ref={searchRef}
                            type="text"
                            placeholder="Pesquisar por nome, nome artístico ou email..."
                            value={searchTerm}
                            onChange={handleSearch}
                            style={{ padding: '6px 0', fontSize: 14 }}
                        />
                    </div>
                </div>
                <div>
                    <select
                        value={distributorFilter}
                        onChange={handleDistributorFilter}
                        style={{
                            padding: '8px 12px', fontSize: 14, borderRadius: 8,
                            border: '1px solid #d1d5db', color: '#374151',
                            background: '#fff', cursor: 'pointer', minWidth: 200
                        }}
                    >
                        <option value="">Todas as distribuidoras</option>
                        <option value="none">Sem distribuidora</option>
                        {distributors.map(d => (
                            <option key={d.distributor_id} value={d.distributor_id}>
                                {d.name} ({d.artist_count})
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* List */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <svg aria-hidden="true" className="inline w-8 h-8 text-gray-200 animate-spin fill-amber-400" viewBox="0 0 100 101" fill="none">
                        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                    </svg>
                    <p className="text-amber-500 font-light text-lg mt-3">A carregar...</p>
                </div>
            ) : artists.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <p className="text-gray-500 text-lg">Nenhum artista encontrado</p>
                </div>
            ) : (
                <>
                    <div className="border rounded-lg max-h-[600px] overflow-y-auto p-5">
                        {artists.map((e) => {
                            const photo = e.photo ? `${CDN_URL}/users/${e.user_id}/profile_photos/${e.photo}` : null;

                            return (
                                <div key={e.user_id} className="border p-3 flex flex-wrap gap-x-4 gap-y-3 flex-row mb-3 items-center rounded-lg hover:bg-gray-50 transition">
                                    <div className="rounded-full bg-gray-300 h-12 w-12 flex-shrink-0 flex items-center justify-center overflow-hidden">
                                        {photo ? (
                                            <img className="h-12 w-12 object-cover rounded-full" src={photo} alt="" />
                                        ) : (
                                            <svg className="text-gray-500 h-12 w-12" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M10 0a10 10 0 1 0 10 10A10.011 10.011 0 0 0 10 0Zm0 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm0 13a8.949 8.949 0 0 1-4.951-1.488A3.987 3.987 0 0 1 9 13h2a3.987 3.987 0 0 1 3.951 3.512A8.949 8.949 0 0 1 10 18Z" />
                                            </svg>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-[220px]">
                                        <div className="flex items-center gap-2">
                                            <h3 className="p-0 m-0 text-left font-semibold text-gray-900">
                                                {e.artist_name || e.name}
                                            </h3>
                                            {e.distributor_name && (
                                                <span style={{
                                                    fontSize: 11, fontWeight: 600,
                                                    background: '#eef2ff', color: '#4f46e5',
                                                    padding: '1px 8px', borderRadius: 12
                                                }}>
                                                    {e.distributor_name}
                                                </span>
                                            )}
                                        </div>
                                        {e.artist_name && e.name !== e.artist_name && (
                                            <p className="p-0 m-0 text-left text-xs text-gray-500">{e.name}</p>
                                        )}
                                        <p className="p-0 m-0 text-left text-sm text-gray-600">{e.email}</p>
                                        <p className="p-0 m-0 text-left text-xs text-gray-400">
                                            ID: {e.user_id} {e.phone ? ` · ${e.phone}` : ''} · {formatDate(e.created_at)}
                                        </p>
                                    </div>
                                    {/* The web Studio (new tab, signed in as the artist) is the main action: it is
                                        where artists work now. The old artist panel stays, quietly, for what
                                        the Studio does not do yet. */}
                                    <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                                        <button type="button" onClick={() => impersonateArtist(e)}
                                            className="px-2 py-1.5 text-sm text-gray-500 hover:text-gray-800 hover:underline"
                                            title="O painel antigo do artista, neste site. Para voltar, usa “Voltar ao Admin” no topo.">
                                            Painel antigo
                                        </button>
                                        <button type="button" onClick={() => setEditArtist(e)}
                                            className="inline-flex items-center gap-1.5 rounded-lg !border !border-solid border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                            title="Editar nome, email, telefone e outros dados da conta">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                            Editar dados
                                        </button>
                                        <button type="button" onClick={() => openArtistInApp(e)}
                                            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium !text-white hover:bg-indigo-700"
                                            title="Abre o web app noutro separador, com a sessão do artista, no Estúdio">
                                            Estúdio na web
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                            <span className="sr-only">(abre noutro separador)</span>
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 mt-4">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage <= 1}
                                className={`text-sm font-medium px-3 py-1.5 rounded border transition ${
                                    currentPage <= 1
                                        ? 'text-gray-300 border-gray-200 cursor-not-allowed'
                                        : 'text-gray-600 border-gray-300 hover:border-amber-400 hover:text-amber-600'
                                }`}
                            >
                                ← Anterior
                            </button>
                            <span className="text-sm text-gray-500">
                                Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong>
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage >= totalPages}
                                className={`text-sm font-medium px-3 py-1.5 rounded border transition ${
                                    currentPage >= totalPages
                                        ? 'text-gray-300 border-gray-200 cursor-not-allowed'
                                        : 'text-gray-600 border-gray-300 hover:border-amber-400 hover:text-amber-600'
                                }`}
                            >
                                Próxima →
                            </button>
                        </div>
                    )}
                </>
            )}
            {editArtist && (
                <EditUserModal
                    user={editArtist}
                    distributors={distributors}
                    onClose={() => setEditArtist(null)}
                    onSaved={() => { setEditArtist(null); fetchArtists(currentPage, searchTerm, distributorFilter); }}
                />
            )}
        </div>
    );
}
